import React, { useContext, useEffect, useState } from 'react';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { SiteContext } from '../../../context/SiteContext';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { setCountryCode } from '../../common/HelperMethods';
import {
  PageAbsolutePath,
  ProductChannel,
  SagePayAccount
} from '../../../consts/Common';
import Opayo from '../../common/SinglePayment/Opayo';
import {
  AddressDetails,
  CardPayment,
  DirectDebitDetails,
  InsuranceJourneyContext,
  InsuranceJourneyState,
  PaymentMethod
} from '../../common/CommonTypes';
import AddressManager from '../../common/Address/AddressManager';
import { CarInsuranceApi } from '../../../api/CarInsuranceApi';
import DirectDebit from '../../common/DirectDebit/DirectDebit';
import PaymentSummary, { getAnnualTotalPayable } from './PaymentSummary';
import { formatNumber } from '../../common/Formatter';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { useErrorBoundary } from 'react-error-boundary';

enum PageState {
  Initial,
  ValidatingAddress,
  UpdatingAddress,
  ValidatingDirectDebit,
  UpdatingDirectDebit,
  TakingCardPayment,
  RegisteringPolicy,
  PolicyRegistered,
  Redirecting,
  ThreeDAuth
}

const initialDirectDebitDetails: DirectDebitDetails = {
  accountHolderName: '',
  sortCode1: '',
  sortCode2: '',
  sortCode3: '',
  accountNumber: '',
  selectedPaymentDay: '',
  bankName: ''
};

const PaymentPage = (): JSX.Element => {
  const { site } = useContext(SiteContext);
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();
  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const {
    personalDetails,
    addressDetails,
    insuranceProducts,
    paymentDetails,
    staticData,
    insuranceNavigationStatus,
    savingQuote
  } = journey;
  const { commonEvents, paymentPage } = pageEvents;
  const { setSavingQuote, onPageChange, setQuote } = commonEvents;
  const { onChangeDirectDebitDetails, setCardPaymentDetails } = paymentPage;
  const [redirect, setRedirect] = useState('');
  const [paymentReady, setPaymentReady] = useState(false);
  const [billingAddress, setBillingAddress] = useState<AddressDetails>(
    addressDetails.overnightParkedAddress
  );
  const [pageState, setPageState] = useState<PageState>(PageState.Initial);

  useEffect(() => {
    if (redirect && pageState === PageState.Redirecting) {
      navigate(redirect);
    } else {
      if (pageState === PageState.PolicyRegistered && journey.policyNumber) {
        setPageState(PageState.Redirecting);
      }
    }
  }, [redirect, pageState, journey.policyNumber]);

  useEffect(() => {
    if (
      paymentDetails?.cardPaymentDetails?.transactionId &&
      pageState === PageState.RegisteringPolicy
    ) {
      RegisterPolicy(setRegistrationSuccess, setRegistrationFailure);
    }
  }, [paymentDetails, pageState]);

  const onPayClick = () => {
    setPageState(PageState.ValidatingAddress);
  };

  const onBackClick = () => {
    if (paymentDetails.selectedPaymentMethod === PaymentMethod.monthly) {
      navigate(PageAbsolutePath.Insurance.SettingUpDirectDebit);
    } else {
      navigate(PageAbsolutePath.Insurance.CheckYourDetails);
    }
  };

  const onAddressUpdate = (address: AddressDetails) => {
    setBillingAddress(address);

    if (paymentDetails.selectedPaymentMethod === PaymentMethod.monthly) {
      setPageState(PageState.ValidatingDirectDebit);
    } else {
      setPageState(PageState.TakingCardPayment);
    }
  };

  const onAddressValidated = (isAddressValid: boolean) => {
    if (isAddressValid) {
      setPageState(PageState.UpdatingAddress);
    } else {
      setPageState(PageState.Initial);
    }
  };

  const onInvalidPaymentForm = () => {
    setPageState(PageState.Initial);
  };

  const cardPaymentAmount =
    paymentDetails.selectedPaymentMethod === PaymentMethod.annually
      ? getAnnualTotalPayable(insuranceProducts)
      : insuranceProducts.monthlyDeposit;

  const payment: CardPayment = {
    amount: cardPaymentAmount,
    description: `${site.brand.brandDisplayFullName} Insurance`,
    surname: personalDetails.lastName,
    firstName: personalDetails.firstName,
    address1: billingAddress.addressLine1 ?? '',
    address2: billingAddress.addressLine2 ?? '',
    city: billingAddress.townOrCity ?? '',
    postCode: billingAddress.postCode ?? '',
    country: billingAddress.country ?? '',
    state: billingAddress.region ?? '',
    phone: personalDetails.mobilePhone,
    email: personalDetails.email
  };

  const onPaymentFailure = () => {
    console.log('onPaymentFailure called');
    setPageState(PageState.Initial);
  };

  const onPaymentSuccess = (transactionId: string) => {
    console.log('Registering policy started');
    setCardPaymentDetails(transactionId);
    setPageState(PageState.RegisteringPolicy);
  };

  const setRegistrationSuccess = (quote: InsuranceJourneyState) => {
    setQuote({
      ...quote,
      staticData: [...staticData],
      insuranceNavigationStatus: { ...insuranceNavigationStatus }
    });

    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.PolicyConfirmation,
      site.countryCode
    );
    setRedirect(path);
    onPageChange(PageAbsolutePath.Insurance.PaymentPage, true);
    setPageState(PageState.PolicyRegistered);
  };

  const setRegistrationFailure = (errorMessage: string) => {
    console.log(`Registration failure - Error message: ${errorMessage}`);
    showBoundary(new Error(errorMessage));
  };

  const RegisterPolicy = async (
    successFunction: (quote: InsuranceJourneyState) => void,
    failureFunction: (errorMessage: string) => void
  ) => {
    const controller = new AbortController();

    await new CarInsuranceApi().RegisterPolicyAsync(
      journey,
      site.user.token,
      successFunction,
      failureFunction,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const enablePay = paymentReady && pageState === PageState.Initial;

  const onDirectDebitValidated = (valid: boolean) => {
    if (valid) {
      setPageState(PageState.UpdatingDirectDebit);
    } else {
      setPageState(PageState.Initial);
    }
  };

  const onUpdateDirectDebitDetails = (
    canContinueToNextPage: boolean,
    directDebit: DirectDebitDetails
  ) => {
    if (canContinueToNextPage) {
      onChangeDirectDebitDetails(directDebit);
      setPageState(PageState.TakingCardPayment);
    } else {
      setPageState(PageState.Initial);
    }
  };

  const cardHeaderText =
    paymentDetails.selectedPaymentMethod === PaymentMethod.monthly
      ? `Please enter your card details for your £${formatNumber(cardPaymentAmount, 2, 'en-GB')} deposit payment`
      : `Please enter your card details for your £${formatNumber(cardPaymentAmount, 2, 'en-GB')} payment`;

  const open =
    pageState === PageState.RegisteringPolicy ||
    pageState === PageState.UpdatingDirectDebit ||
    pageState === PageState.TakingCardPayment ||
    pageState === PageState.ValidatingDirectDebit;

  const getLoadingMessage = () => {
    let message = '';

    switch (pageState) {
      case PageState.RegisteringPolicy:
        message = 'Registering policy, please wait';
        break;
      case (PageState.UpdatingDirectDebit, PageState.ValidatingDirectDebit):
        return 'Checking bank details';
      case PageState.TakingCardPayment:
        return 'Taking card payment';
      default:
        return '';
    }

    if (!message) {
      return <></>;
    }

    return <span className='m2-left'>{message}</span>;
  };

  return (
    <div>
      <h4 className='section-header m3-top m1-bottom'>Payment page</h4>
      <PaymentSummary
        headerText='What you are buying'
        disableTabs={pageState !== PageState.Initial}
        redirectOnMonthlySelected={true}
        salesTelephoneNumber={
          site.brand.brandPhoneNumbers.quotesAndRenewalsNumber
        }
        hideMonthlyIfZero={true}
      />
      {pageState !== PageState.TakingCardPayment && (
        <div className='control m2-bottom'>
          <AddressManager
            findAddressTitle='Billing address'
            initialAddress={billingAddress}
            update={pageState === PageState.UpdatingAddress}
            validate={pageState === PageState.ValidatingAddress}
            token={site.user.token}
            findAddressLabel=''
            onAddressUpdate={onAddressUpdate}
            onAddressValidated={onAddressValidated}
            allowManualEntry
          />
        </div>
      )}
      <Opayo
        token={site.user.token}
        account={SagePayAccount.Protex}
        sagePayBaseUrl={site.sagePayBaseUrl}
        submitPayment={pageState === PageState.TakingCardPayment}
        payment={payment}
        voidTransaction={false}
        countryCode={site.countryCode}
        onError={() => setPaymentReady(false)}
        onLoaded={() => setPaymentReady(true)}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentFailure={onPaymentFailure}
        onPaymentVoided={() => {}}
        onInvalidPaymentForm={onInvalidPaymentForm}
        cardHeaderText={cardHeaderText}
        onThreeDAuth={() => setPageState(PageState.ThreeDAuth)}
      />
      {paymentDetails.selectedPaymentMethod === PaymentMethod.monthly && (
        <div>
          <h4 className='section-header m3-top'>
            Please enter your account details to set up your direct debit
            instalments
          </h4>
          <DirectDebit
            initialDirectDebitDetails={
              paymentDetails?.directDebitDetails ?? initialDirectDebitDetails
            }
            products={[]}
            update={pageState === PageState.UpdatingDirectDebit}
            validate={pageState === PageState.ValidatingDirectDebit}
            token={site.user.token}
            onValidated={onDirectDebitValidated}
            onUpdateDirectDebitDetails={onUpdateDirectDebitDetails}
            showLoading={false}
            directDebitPayee=''
            productChannel={ProductChannel.Insurance}
            culture={site.cultureCode}
            externalLoadingPanel={true}
          />
        </div>
      )}
      <div className='even-space-out flex-row form-nav'>
        <ButtonBack
          name='btn-back'
          text='Back'
          onClick={onBackClick}
          additionalClassNames='btn-back'
        />
        <ButtonContinue
          name='btn-save-later'
          text='Save For Later'
          onClick={() => setSavingQuote(true)}
          additionalClassNames='btn-save'
          disabled={savingQuote}
        />
        <ButtonContinue
          name='btn-next'
          text='Pay Now'
          onClick={onPayClick}
          disabled={!enablePay}
          additionalClassNames='btn-continue'
        />
      </div>
      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={open}>
        <>
          <CircularProgress color='inherit' />
          {getLoadingMessage()}
        </>
      </Backdrop>
    </div>
  );
};

export default PaymentPage;
